'use client';
import Link from 'next/link';
import { useAuthStore } from '@/store/auth.store';
import { useDashboardStore } from '@/store/dashboard.store';
import { useRouter, usePathname } from 'next/navigation';
import ThemeToggle from '@/components/layout/ThemeToggle';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Bell, X, LogOut, User, Menu, MapPin } from 'lucide-react';
import CityPickerModal from '@/components/ui/CityPickerModal';
import MessagesButton from '@/components/layout/MessagesModal';
import { useCityStore } from '@/store/city.store';
import { useState, useRef, useEffect } from 'react';

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [animating, setAnimating] = useState(false);
  const prevCountRef = useRef<number>(0);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const router = useRouter();

  // Лёгкий polling — только счётчик каждые 30с
  const { user: bellUser } = useAuthStore();
  const { data: countData } = useQuery({
    queryKey: ['notifications-count'],
    queryFn: () => api.get('/notifications/unread-count').then(r => r.data.data),
    refetchInterval: 30000,
    enabled: !!bellUser,
  });

  // Данные дропдауна — только при открытии
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['notifications-preview'],
    queryFn: () => api.get('/notifications').then(r => r.data.data),
    enabled: open && !!bellUser,
    staleTime: 10000,
  });

  const unreadCount: number = countData?.count ?? 0;
  const notifications: any[] = listData ?? [];
  const previewList = notifications.filter((n: any) => !n.isRead).slice(0, 5);

  // Анимация при появлении новых уведомлений
  useEffect(() => {
    if (unreadCount > prevCountRef.current && prevCountRef.current !== 0) {
      setAnimating(true);
      setTimeout(() => setAnimating(false), 1000);
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount]);

  const readAll = useMutation({
    mutationFn: () => api.patch('/notifications/read-all'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications-count'] });
      qc.invalidateQueries({ queryKey: ['notifications-preview'] });
      qc.invalidateQueries({ queryKey: ['notifications-all'] });
    },
  });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getNotificationUrl = (n: any): string | null => {
    switch (n.type) {
      case 'BOOKING_PENDING':
      case 'BOOKING_CONFIRMED':
      case 'BOOKING_EXPIRED':
      case 'BOOKING_COMPLETED':
      case 'BOOKING_CANCELLED':
        if (n.payload?.bookingId) return `/bookings/${n.payload.bookingId}`;
        return '/dashboard?section=pro-bookings';
      case 'REQUEST_NEW':
      case 'REQUEST_RESPONSE':
        if (n.payload?.requestId) return `/requests/${n.payload.requestId}`;
        return '/marketplace';
      case 'ORG_INVITE':
        return '/dashboard?section=organization';
      default:
        return null;
    }
  };

  const typeIcon: Record<string, string> = {
    BOOKING_PENDING:   '\u{1F550}',
    BOOKING_CONFIRMED: '\u2705',
    BOOKING_EXPIRED:   '\u23F0',
    BOOKING_COMPLETED: '\u{1F389}',
    BOOKING_CANCELLED: '\u274C',
    REQUEST_NEW:       '\u{1F4CB}',
    REQUEST_RESPONSE:  '\u{1F4AC}',
    ORG_INVITE:        '\u{1F3E2}',
  };

  const handleNotificationClick = (n: any) => {
    api.patch(`/notifications/${n.id}/read`).catch(() => {});
    qc.invalidateQueries({ queryKey: ['notifications-count'] });
    qc.invalidateQueries({ queryKey: ['notifications-preview'] });
    qc.invalidateQueries({ queryKey: ['notifications-all'] });
    const url = getNotificationUrl(n);
    if (url) { router.push(url); setOpen(false); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-text-secondary hover:text-brand transition-colors"
      >
        <Bell
          size={20}
          className={animating ? 'animate-bounce text-brand' : ''}
        />
        {unreadCount > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ${animating ? 'animate-ping' : ''}`}
          >
            {animating ? '' : (unreadCount > 9 ? '9+' : unreadCount)}
          </span>
        )}
        {unreadCount > 0 && animating && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-80 max-w-[calc(100vw-2rem)] bg-bg-surface rounded-2xl shadow-2xl border border-border z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-text-primary">Уведомления</span>
              {unreadCount > 0 && (
                <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full font-bold">{unreadCount}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={() => readAll.mutate()} className="text-xs text-brand hover:underline">
                  Прочитать все
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-text-secondary">
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {listLoading ? (
              <div className="space-y-2 p-3">
                {[1,2,3].map(i => (
                  <div key={i} className="animate-pulse h-14 bg-bg-muted rounded-xl" />
                ))}
              </div>
            ) : previewList.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-8">Нет новых уведомлений</p>
            ) : (
              previewList.map((n: any) => (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className="px-4 py-3 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-bg-subtle transition-colors bg-blue-50/40"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-lg flex-shrink-0">{typeIcon[n.type] ?? '\u{1F514}'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-text-primary">{n.title}</p>
                      <p className="text-xs text-text-secondary mt-0.5 leading-relaxed">{n.body}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(n.createdAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1" />
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="px-4 py-3 border-t border-border-subtle">
            <button
              onClick={() => { router.push('/notifications'); setOpen(false); }}
              className="w-full text-center text-sm text-brand hover:underline font-medium"
            >
              Все уведомления
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AvailabilityToggle() {
  const { isAvailable, setIsAvailable } = useDashboardStore();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (v: boolean) =>
      api.patch('/specialists/me/availability-status', { isAvailable: v }).then(r => r.data.data),
    onSuccess: (data) => {
      setIsAvailable(data.isAvailable);
      qc.invalidateQueries({ queryKey: ['specialist-profile'] });
    },
  });

  return (
    <button
      onClick={() => mutation.mutate(!isAvailable)}
      className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-bg-subtle transition-colors"
      title={isAvailable ? 'Принимаю заявки' : 'Не принимаю заявки'}
    >
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isAvailable ? 'bg-green-500' : 'bg-gray-400'}`} />
      <span className="hidden sm:inline text-xs text-text-secondary">
        {isAvailable ? 'Принимаю' : 'Не принимаю'}
      </span>
    </button>
  );
}

export default function Header() {
  const { user, clearAuth, isAuthenticated } = useAuthStore();
  const { isSpecialist, setIsSpecialist, setIsAvailable } = useDashboardStore();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { city, setHasChosen } = useCityStore();
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Загружаем профиль специалиста для синхронизации store
  useQuery({
    queryKey: ['specialist-profile'],
    queryFn: () =>
      api.get('/specialists/me/profile').then(r => {
        const data = r.data.data;
        setIsSpecialist(true, data.id);
        setIsAvailable(data.isAvailable ?? true);
        return data;
      }).catch(() => {
        setIsSpecialist(false);
        return null;
      }),
    enabled: isAuthenticated(),
  });

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    clearAuth();
    router.push('/');
    setTimeout(() => queryClient.clear(), 100);
  };



  const isAdmin = user?.role === 'ADMIN';
  const onDashboard = pathname === '/dashboard';

  return (
    <header className="bg-bg-surface border-b border-border sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 h-14 sm:h-16 flex items-center justify-between gap-3">

        {/* Логотип */}
        <div className="flex flex-col flex-shrink-0">
          <Link href={isAdmin ? '/admin' : '/'} className="text-lg sm:text-xl font-bold text-brand hover:text-brand-dark transition-colors leading-tight">
            СвойПрофи
          </Link>
          <span className="hidden sm:block text-[10px] text-gray-400 leading-tight -mt-0.5">маркетплейс услуг</span>
        </div>

        {/* Город */}
        <button
          onClick={() => setHasChosen(false)}
          className="hidden sm:flex items-center gap-1 text-sm text-gray-500 hover:text-brand transition-colors flex-shrink-0"
        >
          <MapPin size={14} />
          <span>{city ?? 'Выбрать город'}</span>
        </button>
        <CityPickerModal />
        {/* Правая часть навигации */}
        <nav className="flex items-center gap-1 sm:gap-2 ml-auto">
          {isAuthenticated() ? (
            <>
              {isAdmin && (
                <Link href="/admin" className="text-text-ui hover:text-brand text-sm font-medium transition-colors hidden sm:inline">
                  Админ панель
                </Link>
              )}



              <ThemeToggle />
              <MessagesButton />
              <NotificationBell />
              <Link href="/marketplace" className="hidden sm:inline text-sm text-text-ui font-medium hover:text-brand transition-colors px-2 py-1">
                Биржа заказов
              </Link>
              <Link href="/blog" className="hidden sm:inline text-sm text-text-ui font-medium hover:text-brand transition-colors px-2 py-1">
                Блог
              </Link>

              {/* Имя пользователя */}
              <Link href="/dashboard" className="hidden sm:inline text-sm text-text-ui font-medium hover:text-brand transition-colors">
                {user?.firstName}
              </Link>

              {/* Иконка профиля на мобильном */}
              <Link href="/dashboard" className="sm:hidden p-2 text-text-secondary hover:text-brand transition-colors rounded-lg hover:bg-bg-subtle">
                <User size={18} />
              </Link>

              <button
                onClick={handleLogout}
                className="flex items-center gap-1 text-red-600 hover:text-red-800 transition-colors p-2 rounded-lg hover:bg-red-50"
              >
                <LogOut size={18} className="sm:hidden" />
                <span className="hidden sm:inline text-sm font-medium">Выйти</span>
              </button>
            </>
          ) : (
            <>
              <ThemeToggle />
              <Link href="/marketplace" className="hidden sm:inline text-text-ui hover:text-brand text-sm font-medium transition-colors px-2 py-1">
                Биржа заказов
              </Link>
              <Link href="/for-business" className="hidden sm:inline text-text-ui hover:text-brand text-sm font-medium transition-colors px-2 py-1">
                Для бизнеса
              </Link>
              <Link href="/blog" className="hidden sm:inline text-text-ui hover:text-brand text-sm font-medium transition-colors px-2 py-1">
                Блог
              </Link>
              <Link href="/login" className="hidden sm:inline text-text-ui hover:text-brand text-sm font-medium transition-colors px-2 py-1">
                Войти
              </Link>
              <Link
                href="/register"
                style={{ color: '#ffffff', backgroundColor: 'var(--color-brand)' }}
                className="hidden sm:inline-block px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-all shadow-sm"
              >
                Регистрация
              </Link>
              {/* Бургер — только мобильный, только для неавторизованных */}
              <button
                className="sm:hidden p-2 text-text-secondary hover:text-brand transition-colors rounded-lg hover:bg-bg-subtle"
                onClick={() => setMobileMenuOpen(v => !v)}
                aria-label="Меню"
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Мобильное меню для неавторизованных */}
      {!isAuthenticated() && mobileMenuOpen && (
        <div className="sm:hidden border-t border-border-subtle bg-bg-surface shadow-lg">
          <div className="flex flex-col px-4 py-3 gap-1">
            <Link
              href="/marketplace"
              onClick={() => setMobileMenuOpen(false)}
              className="py-3 px-2 text-text-ui font-medium text-sm border-b border-gray-50 hover:text-brand transition-colors"
            >
              Биржа заказов
            </Link>
            <Link
              href="/pricing"
              onClick={() => setMobileMenuOpen(false)}
              className="py-3 px-2 text-text-ui font-medium text-sm border-b border-gray-50 hover:text-brand transition-colors"
            >
              Тарифы
            </Link>
            <Link
              href="/blog"
              onClick={() => setMobileMenuOpen(false)}
              className="py-3 px-2 text-text-ui font-medium text-sm border-b border-gray-50 hover:text-brand transition-colors"
            >
              Блог
            </Link>
            <Link
              href="/login"
              onClick={() => setMobileMenuOpen(false)}
              className="py-3 px-2 text-text-ui font-medium text-sm border-b border-gray-50 hover:text-brand transition-colors"
            >
              Войти
            </Link>
            <Link
              href="/register"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-2 py-3 px-2 text-center rounded-xl text-sm font-semibold"
              style={{ backgroundColor: 'var(--color-brand)', color: '#ffffff' }}
            >
              Зарегистрироваться
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
